import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type { Segment as ApiSegment } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Segment entity — workspace-scoped audiences.
 *
 * Minimal detail-only entity: id normalized to `string` (layer-wide
 * convention even though the wire ships a number) and the epoch timestamps
 * transformed to `Date`. List/mutation surface can be added when a consumer
 * needs it; today only the by-id detail read exists.
 */
export type Segment = Omit<ApiSegment, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

/**
 * Which icon stands for a segment: the shape of its membership, not its name.
 *
 * A manual segment holds whoever was put in it, a snapshot is a query answered once, and every
 * other kind is a query the workspace keeps answering. The embedded app's `icon` getter
 * (`models/segment.ts`) draws the same three. `static` is the wire's own word for manual, which is
 * why this reads it rather than the word the product uses.
 */
export function segmentIconName(segment: Pick<Segment, 'type'>): 'segment-manual' | 'reset' | 'segment-data-driven' {
  if (segment.type === 'static') return 'segment-manual';
  if (segment.type === 'snapshot') return 'reset';
  return 'segment-data-driven';
}

const SegmentFromApi = readSchema<Segment>(
  z.looseObject({
    id: z.number().transform(String),
    created_at: epochSecondsToDate,
    updated_at: epochSecondsToDate,
  }),
);

/** @public */
export const segment = defineEntity<Segment>({
  name: 'segment',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SegmentFromApi },
});

export const segmentKeys = {
  ...segment.keys,
  /** Live member count (`segments/{id}/count`), cached as the bare number. */
  count: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'count', id] as const,
  /**
   * The same endpoint cached as `{ count, finished }`, for callers that poll
   * until the count settles. Keyed apart from `count` because the two cache
   * different value shapes for the same URL.
   */
  countStatus: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'count-status', id] as const,
  /** Segment metrics (`segments/{id}/metrics`). */
  metrics: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'metrics', id] as const,
  /** Build status (`segments/{id}/status`). */
  status: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'status', id] as const,
  /** Per-segment usage (`segments/{id}/usage`). */
  usage: (workspaceId: number, id: string) => [...segment.keys.all(workspaceId), 'usage', id] as const,
  /** Bulk usage map (`segments_usage?segment_ids=…`), keyed by the sorted id set. */
  usageBulk: (workspaceId: number, ids: readonly string[]) =>
    [...segment.keys.all(workspaceId), 'usage', 'bulk', [...ids].sort()] as const,
  /** Server-side manual-segment search (the add-to-segment picker), keyed by term. */
  manualOptions: (workspaceId: number, search: string) =>
    [...segment.keys.all(workspaceId), 'manual-options', search] as const,
  /** How many manual segments the workspace has, which decides a default rather than a view. */
  manualCount: (workspaceId: number) => [...segment.keys.all(workspaceId), 'manual-count'] as const,
  /**
   * The short list (`segments?short=1`). Under `lists`, so whatever changes
   * which segments a list holds refreshes this one too.
   */
  shortList: (workspaceId: number) => [...segment.keys.lists(workspaceId), 'short'] as const,
  /**
   * The segments linked to forms (`segments?linked_form_id=`), archived ones
   * included, keyed by the sorted form ids. Under `lists`, so whatever changes
   * which segments a list holds refreshes this one too.
   */
  linkedToForms: (workspaceId: number, formIds: readonly string[]) =>
    [...segment.keys.lists(workspaceId), 'linked-forms', [...formIds].sort()] as const,
  /** One page of the workspace's segments with the total (`segments?page=&size=`), for a paged table. */
  page: (workspaceId: number, page: number) => [...segment.keys.lists(workspaceId), 'page', page] as const,
  /** The segments a person belongs to (`customers/{id}/segments`). Under `lists`, as membership changes it. */
  forCustomer: (workspaceId: number, customerId: string) =>
    [...segment.keys.lists(workspaceId), 'customer', customerId] as const,
};

export function parseSegment(raw: ApiSegment): Segment {
  return segment.parse(raw);
}
